import React, { FC } from 'react'
import classNames from 'classnames'
import { Plural, t } from '@lingui/macro'

import { MoveDirectionEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconDoc from '../../../components/common/icon/IconDoc'

import { IPathway } from '../types'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import IconLock from '../../../components/common/icon/IconLock'
import Tags from '../../../components/common/tag/Tags'
import { ButtonIcon, Text, Box } from 'front-end/src/components'
import { PathwayStateEnum } from '../../../../../types/Pathway'
import { IconArrowUp } from '../../../components/common/icon/IconArrowUp'
import { IconArrowDown } from '../../../components/common/icon/IconArrowDown'
import { useMovePathwayMutation } from './Item.codegen'
import { dayjsFromNow } from 'shared/dayjs'
import { brandColors } from 'shared/basicPalette'
import { StatusDraftGrey } from 'front-end/src/components/common/status/Status'
import IconDuplicate from 'front-end/src/components/common/icon/IconDuplicate'

import './PathwayItem.less'

interface IPathwayItemProps {
  pathway: IPathway
  first?: boolean
  last?: boolean
  canReorder: boolean
  onClone: () => void
  onChange?: () => void
}

export const PathwayResourcesPlural = ({ value }) => {
  return (
    <Plural
      _0="No resources"
      one="# resource"
      other="# resources"
      value={value}
    />
  )
}

export const PathwayItem: FC<IPathwayItemProps> = ({
  pathway,
  onClone,
  onChange,
  first,
  last,
  canReorder
}) => {
  const [move] = useMovePathwayMutation()

  return (
    <div
      className={classNames('ListItem Pathways__item', {
        'is-draft': pathway.state === PathwayStateEnum.DRAFT
      })}
      onClick={() => {
        navigateTo(linkBuilder.pathwayDetailManage(pathway.id))
      }}
    >
      <div className="Pathways__item-title">
        {pathway.title}
        {!pathway.is_public && pathway.state !== PathwayStateEnum.DRAFT && (
          <IconLock className="Pathways__item-private" />
        )}
        {pathway.state === PathwayStateEnum.DRAFT && (
          <StatusDraftGrey ml="8px" alignSelf="flex-end" />
        )}
      </div>
      <div className="Pathways__item-topics">
        <IconDoc size="sm" />
        <span>
          <PathwayResourcesPlural value={pathway.currentTopicVersionsCount} />
        </span>
      </div>
      <div className="Pathways__item-tags">
        {/* @ts-expect-error  9/11/2020*/}
        <Tags items={pathway.tags} />
      </div>

      {canReorder ? (
        <Box className="Pathways__item-order" color={brandColors.grey[200]}>
          {!first && (
            <ButtonIcon
              circular
              size="sm"
              aria-label={t`Up`}
              hint={t`Up`}
              icon={<IconArrowUp />}
              onClick={async (e) => {
                e.stopPropagation()
                await move({
                  variables: {
                    pathwayId: pathway.id,
                    direction: MoveDirectionEnum.UP
                  }
                })
                onChange && onChange()
              }}
            />
          )}

          {!last && (
            <ButtonIcon
              circular
              size="sm"
              aria-label={t`Down`}
              hint={t`Down`}
              icon={<IconArrowDown />}
              onClick={async (e) => {
                e.stopPropagation()
                await move({
                  variables: {
                    pathwayId: pathway.id,
                    direction: MoveDirectionEnum.DOWN
                  }
                })
                onChange && onChange()
              }}
            />
          )}
        </Box>
      ) : (
        <Text color={brandColors.grey.light}>
          {dayjsFromNow(pathway.created_at)}
        </Text>
      )}

      <ButtonIcon
        circular
        className="Pathways__item-duplicate"
        hint={t`Create a copy`}
        aria-label={t`Create a copy`}
        borderTopRightRadius={0}
        borderBottomRightRadius={0}
        onClick={(e) => {
          e.stopPropagation()
          onClone()
        }}
        icon={<IconDuplicate color={brandColors.grey[500]} boxSize="16px" />}
      />
    </div>
  )
}
